import React from "react";
import { moveArrayElement } from "shared-utils";

import { IMultiSelectItem } from "./MultiSelectItem";
import { MultiSelectAutocomplete } from "./MultiSelectAutocomplete";
import { MultiSelectListItems } from "./MultiSelectListItems";
import { MultiSelectDropdown } from "./MultiSelectDropdown";
import { ITooltipContent } from "../../Molecules/Tooltip/Tooltip";
import "./MultiSelectList.scss";

export interface IMultiSelectListContent {
  label: string;
  placeholder: string;
  tooltip?: ITooltipContent;
  annex?: JSX.Element;
}

interface ICommonProps<TValue> {
  name: string;
  selectedItems: IMultiSelectItem<TValue>[];
  handleChanges: (items: IMultiSelectItem<TValue>[]) => void;
  isDisabled?: boolean;
  content: IMultiSelectListContent;
  invalid?: boolean;
  validationMessage?: string;
  hideMoveControls?: boolean;
}

interface IAutocompleteType<TValue> extends ICommonProps<TValue> {
  delayLoadTime?: number;
  list?: never;
  type: "autocomplete";
  loadData?: (value: string) => Promise<IMultiSelectItem<TValue>[]>;
}

interface IDropdownType<TValue> extends ICommonProps<TValue> {
  list: IMultiSelectItem<TValue>[];
  type: "dropdown";
  delayLoadTime?: never;
  loadData?: never;
}

export type MultiSelectListProps<TValue> =
  | IAutocompleteType<TValue>
  | IDropdownType<TValue>;

export default class MultiSelectList<TValue> extends React.Component<
  MultiSelectListProps<TValue>
> {
  public static readonly defaultProps = {
    isDisabled: false,
    delayLoadTime: 0,
  };

  public render() {
    const { selectedItems, hideMoveControls } = this.props;

    return (
      <div className="c-multi-select-list">
        <div className="c-multi-select-list__input">{this.renderInput()}</div>
        <MultiSelectListItems
          hideMoveControls={hideMoveControls}
          selectedItems={selectedItems}
          moveElementDown={this.moveElementDown}
          moveElementUp={this.moveElementUp}
          removeItem={this.removeItem}
        />
      </div>
    );
  }

  private readonly renderInput = () => {
    const {
      type,
      isDisabled,
      content,
      name,
      delayLoadTime,
      loadData,
      selectedItems,
      invalid,
      validationMessage,
      list,
    } = this.props;

    let input: React.ReactNode = null;
    if (!isDisabled) {
      if (type === "autocomplete") {
        input = (
          <MultiSelectAutocomplete
            {...this.props}
            content={content}
            name={name}
            invalid={invalid}
            validationMessage={validationMessage}
            loadData={loadData || (() => Promise.resolve([]))}
            delayLoadTime={delayLoadTime}
            selectItem={this.selectItem}
          />
        );
      } else if (type === "dropdown") {
        input = (
          <MultiSelectDropdown
            {...this.props}
            content={content}
            name={name}
            invalid={invalid}
            validationMessage={validationMessage}
            list={list || []}
            selectedItems={selectedItems}
            selectItem={this.selectItem}
          />
        );
      }
    }

    return input;
  };

  private readonly selectItem = (item: IMultiSelectItem<TValue>) => {
    const { selectedItems, handleChanges } = this.props;

    handleChanges([...selectedItems, item]);
  };

  private readonly moveElementUp = (value: TValue) => {
    const { selectedItems, handleChanges } = this.props;

    const fromIndex = selectedItems.findIndex((item) => item.value === value);

    if (fromIndex > 0) {
      const updatedList = moveArrayElement(
        selectedItems,
        fromIndex,
        fromIndex - 1
      );
      handleChanges(updatedList);
    }
  };

  private readonly moveElementDown = (value: TValue) => {
    const { selectedItems, handleChanges } = this.props;
    const fromIndex = selectedItems.findIndex((item) => item.value === value);

    if (fromIndex < selectedItems.length - 1) {
      const updatedList = moveArrayElement(
        selectedItems,
        fromIndex,
        fromIndex + 1
      );
      handleChanges(updatedList);
    }
  };

  private readonly removeItem = (value: TValue) => {
    const { selectedItems, handleChanges } = this.props;

    const updatedList = selectedItems.filter((item) => item.value !== value);

    handleChanges(updatedList);
  };
}
